import React, { useMemo, useCallback, useState } from "react";
import {
  Paper,
  Stack,
  Typography,
  CircularProgress,
  Alert,
  Divider,
} from "@mui/material";
import ActivityCandidateGroupCard from "../cards/ActivityCandidateGroupCard";
import CreateHoldingGroupDialog from "../../dialogs/CreateHoldingGroupDialog";
import { isHoldingGroup } from "../../utils/activityBoardUtils";

function buildPersonKey(person) {
  return `${String(person?.personType).toUpperCase()}:${person?.personId}`;
}

function normaliseMember(member) {
  return {
    personId: member.personId,
    personType: member.personType,
    name: member.name,
  };
}

function isArriverToday(person) {
  return person?.arrivingToday === 1;
}

function isLeaverToday(person) {
  return person?.departingToday === 1;
}

function isArriverOrLeaverToday(person) {
  return isArriverToday(person) || isLeaverToday(person);
}

export default function ActivityCandidatesPanel({
  selectedCampusName,
  selectedCampusLabel,
  selectedSlot,
  unbookedCandidates = [],
  bookedCandidateGroups = [],
  candidatesLoading,
  candidatesError,
  activityGroups = [],
  onAssignMembers,
  arriversHoldingGroup = null,
  departersHoldingGroup = null,
  leaders = [],
  onCreateHoldingGroup,
}) {
  const [createDialog, setCreateDialog] = useState({
    open: false,
    kind: null,
    members: [],
  });

  const assignableGroups = useMemo(
    () =>
      (activityGroups || []).filter((group) => {
        const sameSlot =
          String(group?.slot || "").toUpperCase() ===
          String(selectedSlot || "").toUpperCase();

        const currentCount = Array.isArray(group?.members) ? group.members.length : 0;
        const capacity = group?.capacity;
        const hasSpace = capacity == null || currentCount < capacity;

        return sameSlot && hasSpace;
      }),
    [activityGroups, selectedSlot]
  );

  // Route enforcement is one-directional: NON-arrivers can never target a holding
  // group (they're stripped from the normal cards' dropdowns), so nobody gets
  // mixed into the wrong group. Arrivers/departers themselves stay flexible — they
  // can go into their own holding group OR a normal activity (e.g. the evening
  // program on their arrival day). Note normalAssignableGroups excludes BOTH
  // holding groups, so an arriver still can't land in the departers group.
  const normalAssignableGroups = useMemo(
    () => assignableGroups.filter((group) => !isHoldingGroup(group)),
    [assignableGroups]
  );

  // A holding group is only offered as a target on its own slot. The group shows
  // on every slot (middle column), but assigning to it from another slot would
  // add people the current slot's candidate feed can't see as placed, so they'd
  // bounce back into the card. On other slots, arrivers/departers go into the
  // normal program instead (e.g. evening activities on arrival day).
  const isHoldingGroupOnThisSlot = useCallback(
    (group) =>
      group &&
      String(group.slot || "").toUpperCase() === String(selectedSlot || "").toUpperCase(),
    [selectedSlot]
  );

  const arriversEligible = useMemo(
    () =>
      isHoldingGroupOnThisSlot(arriversHoldingGroup)
        ? [arriversHoldingGroup, ...normalAssignableGroups]
        : normalAssignableGroups,
    [arriversHoldingGroup, normalAssignableGroups, isHoldingGroupOnThisSlot]
  );

  const departersEligible = useMemo(
    () =>
      isHoldingGroupOnThisSlot(departersHoldingGroup)
        ? [departersHoldingGroup, ...normalAssignableGroups]
        : normalAssignableGroups,
    [departersHoldingGroup, normalAssignableGroups, isHoldingGroupOnThisSlot]
  );

  const openCreateDialog = useCallback((kind, members) => {
    setCreateDialog({ open: true, kind, members: members || [] });
  }, []);

  const closeCreateDialog = useCallback(
    () => setCreateDialog((prev) => ({ ...prev, open: false })),
    []
  );

  const handleConfirmCreate = useCallback(
    async ({ leaderStaffId }) => {
      await onCreateHoldingGroup?.({
        kind: createDialog.kind,
        members: createDialog.members,
        leaderStaffId,
      });
      closeCreateDialog();
    },
    [onCreateHoldingGroup, createDialog.kind, createDialog.members, closeCreateDialog]
  );

  const activityGroupsById = useMemo(() => {
    const map = new Map();
    (activityGroups || []).forEach((group) => {
      map.set(group.groupId, group);
    });
    return map;
  }, [activityGroups]);

  const buildMergedMembers = useCallback(
    (targetGroupId, additions) => {
      const targetGroup = activityGroupsById.get(targetGroupId);
      const existingMembers = Array.isArray(targetGroup?.members)
        ? targetGroup.members.map(normaliseMember)
        : [];

      const merged = [...existingMembers];
      const existingKeys = new Set(existingMembers.map(buildPersonKey));

      (additions || []).forEach((member) => {
        const normalised = normaliseMember(member);
        const key = buildPersonKey(normalised);

        if (!existingKeys.has(key)) {
          merged.push(normalised);
          existingKeys.add(key);
        }
      });

      return merged;
    },
    [activityGroupsById]
  );

  const handleAssignAll = useCallback(
    (targetGroupId, membersToAdd) => {
      const additions = (membersToAdd || []).map(normaliseMember);
      const nextMembers = buildMergedMembers(targetGroupId, additions);
      onAssignMembers?.(targetGroupId, nextMembers);
    },
    [buildMergedMembers, onAssignMembers]
  );

  // Arrivers and leavers are always pulled out of the main candidate lists and
  // shown in their own sublists below.
  const filteredUnbookedCandidates = useMemo(
    () => (unbookedCandidates || []).filter((person) => !isArriverOrLeaverToday(person)),
    [unbookedCandidates]
  );

  const filteredBookedCandidateGroups = useMemo(
    () =>
      (bookedCandidateGroups || [])
        .map((group) => ({
          ...group,
          members: (group.members || []).filter((member) => !isArriverOrLeaverToday(member)),
        }))
        .filter((group) => (group.members || []).length > 0),
    [bookedCandidateGroups]
  );

  // Every candidate (booked or not), flattened and deduped, so arrivers and
  // leavers can be collected regardless of their booking status.
  const allCandidates = useMemo(() => {
    const booked = (bookedCandidateGroups || []).flatMap((group) => group.members || []);
    const combined = [...(unbookedCandidates || []), ...booked];

    const seen = new Set();
    return combined.filter((person) => {
      const key = buildPersonKey(person);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [unbookedCandidates, bookedCandidateGroups]);

  const arrivers = useMemo(
    () => allCandidates.filter(isArriverToday),
    [allCandidates]
  );

  // Someone arriving and leaving on the same day shows only under arrivers.
  const leavers = useMemo(
    () => allCandidates.filter((person) => isLeaverToday(person) && !isArriverToday(person)),
    [allCandidates]
  );

  const unbookedGroup = useMemo(
    () => ({
      groupKey: "UNBOOKED",
      productId: null,
      productName: null,
      groupName: "No booked activity",
      kindLabel: "Unbooked",
      members: filteredUnbookedCandidates,
    }),
    [filteredUnbookedCandidates]
  );

  const arriversGroup = useMemo(
    () => ({
      groupKey: "ARRIVING_TODAY",
      productId: null,
      productName: null,
      groupName: "Arriving today",
      kindLabel: "Arrivers",
      members: arrivers,
    }),
    [arrivers]
  );

  const leaversGroup = useMemo(
    () => ({
      groupKey: "LEAVING_TODAY",
      productId: null,
      productName: null,
      groupName: "Leaving today",
      kindLabel: "Leavers",
      members: leavers,
    }),
    [leavers]
  );

  if (candidatesLoading) {
    return (
      <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={18} />
          <Typography variant="body2">Loading candidates…</Typography>
        </Stack>
      </Paper>
    );
  }

  if (candidatesError) {
    return (
      <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
        <Alert severity="error">{String(candidatesError?.message || candidatesError)}</Alert>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
      <Stack spacing={1}>
        <Stack spacing={0.5}>
          <Typography fontWeight={800}>
            Candidates {selectedCampusName ? `• ${selectedCampusLabel}` : ""}
          </Typography>

          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            {selectedSlot}
          </Typography>
        </Stack>

        <Divider />

        {filteredUnbookedCandidates.length === 0 &&
        filteredBookedCandidateGroups.length === 0 &&
        arrivers.length === 0 &&
        leavers.length === 0 ? (
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            No candidates for this selection.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {filteredUnbookedCandidates.length > 0 && (
              <ActivityCandidateGroupCard
                key="UNBOOKED"
                group={unbookedGroup}
                eligibleGroups={normalAssignableGroups}
                onAssignAll={(targetGroupId) => {
                  handleAssignAll(targetGroupId, unbookedGroup.members || []);
                }}
                onAssignSelected={(targetGroupId, selectedPeople) => {
                  handleAssignAll(targetGroupId, selectedPeople || []);
                }}
              />
            )}

            {filteredBookedCandidateGroups.map((group) => (
              <ActivityCandidateGroupCard
                key={group.productId || group.productName}
                group={group}
                eligibleGroups={normalAssignableGroups}
                onAssignAll={(targetGroupId) => {
                  handleAssignAll(targetGroupId, group.members || []);
                }}
                onAssignSelected={(targetGroupId, selectedPeople) => {
                  handleAssignAll(targetGroupId, selectedPeople || []);
                }}
              />
            ))}

            {arrivers.length > 0 && (
              <ActivityCandidateGroupCard
                key="ARRIVING_TODAY"
                group={arriversGroup}
                eligibleGroups={arriversEligible}
                onAssignAll={(targetGroupId) => {
                  handleAssignAll(targetGroupId, arriversGroup.members || []);
                }}
                onAssignSelected={(targetGroupId, selectedPeople) => {
                  handleAssignAll(targetGroupId, selectedPeople || []);
                }}
                onCreateGroup={
                  onCreateHoldingGroup && !arriversHoldingGroup
                    ? () => openCreateDialog("ARRIVERS", arrivers)
                    : undefined
                }
                createGroupLabel="Create arrivers group"
              />
            )}

            {leavers.length > 0 && (
              <ActivityCandidateGroupCard
                key="LEAVING_TODAY"
                group={leaversGroup}
                eligibleGroups={departersEligible}
                onAssignAll={(targetGroupId) => {
                  handleAssignAll(targetGroupId, leaversGroup.members || []);
                }}
                onAssignSelected={(targetGroupId, selectedPeople) => {
                  handleAssignAll(targetGroupId, selectedPeople || []);
                }}
                onCreateGroup={
                  onCreateHoldingGroup && !departersHoldingGroup
                    ? () => openCreateDialog("DEPARTERS", leavers)
                    : undefined
                }
                createGroupLabel="Create departers group"
              />
            )}
          </Stack>
        )}
      </Stack>

      <CreateHoldingGroupDialog
        open={createDialog.open}
        onClose={closeCreateDialog}
        kind={createDialog.kind}
        members={createDialog.members}
        leaders={leaders}
        campusLabel={selectedCampusLabel}
        slot={selectedSlot}
        onConfirm={handleConfirmCreate}
      />
    </Paper>
  );
}